// pages/salesReturns/SalesReturnList.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Eye, 
    Edit, 
    Trash2, 
    PlusCircle,
    Download,
    Printer,
    Filter,
    Search,
    ArrowUp,
    ArrowDown,
    ChevronLeft,
    ChevronRight,
  
    RefreshCw,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Clock
} from 'react-feather';
import Select from 'react-select';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { saleReturnActions } from '../../core/action/saleReturnActions';
import ViewSaleReturn from '../../core/modals/salesReturns/ViewSaleReturn';
import EditSaleReturn from '../../core/modals/salesReturns/EditSaleReturn';

const { RangePicker } = DatePicker;

const SalesReturnList = () => {
    const navigate = useNavigate();
    
    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Filters
    const [filters, setFilters] = useState({
        status: '',
        customer_id: 0,
        date_from: '',
        date_to: ''
    });
    
    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    
    // Modal state
    const [modalType, setModalType] = useState(null); // 'view', 'edit'
    const [selectedReturn, setSelectedReturn] = useState(null);
    
    // Sort
    const [sortConfig, setSortConfig] = useState({ 
        key: 'return_date', 
        direction: 'desc'
    });

    const isInitialMount = useRef(true);
    const prevPageRef = useRef(1);

    // Status options
    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'received', label: 'Received' },
        { value: 'completed', label: 'Completed' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Customer options
    const [customers, setCustomers] = useState([]);

    // Load customers
    const loadCustomers = async () => {
        try {
            const result = await saleReturnActions.getCustomers({ limit: 1000 });
            if (result.success) {
                setCustomers([
                    { value: 0, label: 'All Customers' },
                    ...result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} (${c.phone || 'No phone'})`
                    }))
                ]);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    useEffect(() => {
        loadCustomers();
    }, []);

    // Main fetch function
    const fetchReturns = async (page = pagination.page) => {
        try {
            setLoading(true);
            
            const params = {
                page: page,
                limit: pagination.limit,
                sort_by: sortConfig.key,
                sort_order: sortConfig.direction.toUpperCase(),
                ...(searchTerm && { search: searchTerm }),
                ...(filters.status && { status: filters.status }),
                ...(filters.customer_id > 0 && { customer_id: filters.customer_id }),
                ...(filters.date_from && { date_from: filters.date_from }),
                ...(filters.date_to && { date_to: filters.date_to })
            };
            
            const result = await saleReturnActions.getReturns(params);
            
            if (result.success) {
                setReturns(result.data || []);
                setPagination(prev => ({
                    ...prev,
                    page: result.page || page,
                    total: result.total,
                    totalPages: result.totalPages || Math.ceil(result.total / pagination.limit)
                }));
                prevPageRef.current = page;
            } else {
                Swal.fire('Error', result.message || 'Failed to load returns', 'error');
                setReturns([]);
            }
        } catch (error) {
            console.error('Error fetching returns:', error);
            Swal.fire('Error', 'Failed to load returns', 'error');
            setReturns([]);
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchReturns(1);
        isInitialMount.current = false;
    }, []);

    // Fetch when page changes
    useEffect(() => {
        if (isInitialMount.current || prevPageRef.current === pagination.page) return;
        fetchReturns(pagination.page);
    }, [pagination.page]);

    // Fetch when filters or sort change
    useEffect(() => {
        if (isInitialMount.current) return;
        
        const timer = setTimeout(() => {
            setPagination(prev => ({ ...prev, page: 1 }));
            setTimeout(() => fetchReturns(1), 0);
        }, 300);
        
        return () => clearTimeout(timer);
    }, [sortConfig, searchTerm, filters]);

    // Handle sort
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Modal handlers
    const handleView = (returnData) => {
        setSelectedReturn(returnData);
        setModalType('view');
    };

    const handleEdit = (returnData) => {
        setSelectedReturn(returnData);
        setModalType('edit');
    };

    const handleModalClose = () => {
        setModalType(null);
        setSelectedReturn(null);
    };

    const handleSuccess = () => {
        fetchReturns(pagination.page);
        handleModalClose();
    };

    // Handle delete
    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Delete Return?',
            text: 'Are you sure you want to delete this return? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const deleteResult = await saleReturnActions.deleteReturn(id);
                    if (deleteResult.success) {
                        Swal.fire('Deleted!', 'Return has been deleted.', 'success');
                        fetchReturns(pagination.page);
                    } else {
                        Swal.fire('Error', deleteResult.message || 'Failed to delete return', 'error');
                    }
                } catch (error) {
                    console.error('Error deleting return:', error);
                    Swal.fire('Error', 'Failed to delete return', 'error');
                }
            }
        });
    };

    // Handle status update
    // const handleStatusUpdate = async (id, newStatus) => {
    //     try {
    //         const result = await saleReturnActions.updateStatus(id, newStatus);
    //         if (result.success) {
    //             Swal.fire('Success', `Status updated to ${newStatus}`, 'success');
    //             fetchReturns(pagination.page);
    //         } else {
    //             Swal.fire('Error', result.message || 'Failed to update status', 'error');
    //         }
    //     } catch (error) {
    //         console.error('Error updating status:', error);
    //         Swal.fire('Error', 'Failed to update status', 'error');
    //     }
    // };

    // Handle page change
    const handlePageChange = (pageNumber) => {
        if (pageNumber !== pagination.page) {
            setPagination(prev => ({ ...prev, page: pageNumber }));
        }
    };

    const handleNextPage = () => {
        if (pagination.page < pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: prev.page + 1 }));
        }
    };

    const handlePrevPage = () => {
        if (pagination.page > 1) {
            setPagination(prev => ({ ...prev, page: prev.page - 1 }));
        }
    };

    // Handle search
    const handleSearchInput = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            setPagination(prev => ({ ...prev, page: 1 }));
        }
    };

    // Reset filters
    const resetFilters = () => {
        setSearchTerm('');
        setFilters({
            status: '',
            customer_id: 0,
            date_from: '',
            date_to: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            setFilters(prev => ({
                ...prev,
                date_from: dates[0].format('YYYY-MM-DD'),
                date_to: dates[1].format('YYYY-MM-DD')
            }));
        } else {
            setFilters(prev => ({
                ...prev,
                date_from: '',
                date_to: ''
            }));
        }
    };

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
    };

    // Format functions
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format('DD/MM/YYYY');
    };

    const getStatusBadge = (status) => {
        const badges = {
            'pending': 'badge bg-warning',
            'approved': 'badge bg-info',
            'received': 'badge bg-primary',
            'completed': 'badge bg-success',
            'rejected': 'badge bg-danger',
            'cancelled': 'badge bg-secondary'
        };
        return badges[status?.toLowerCase()] || 'badge bg-secondary';
    };

    const getStatusIcon = (status) => {
        const icons = {
            'pending': <Clock size={14} />,
            'approved': <CheckCircle size={14} />,
            'received': <RefreshCw size={14} />,
            'completed': <CheckCircle size={14} />,
            'rejected': <XCircle size={14} />,
            'cancelled': <XCircle size={14} />
        };
        return icons[status?.toLowerCase()] || <AlertTriangle size={14} />;
    };

    const getPaymentStatusBadge = (status) => {
        const badges = {
            'unpaid': 'badge bg-danger',
            'partial': 'badge bg-warning',
            'paid': 'badge bg-success'
        };
        return badges[status?.toLowerCase()] || 'badge bg-secondary';
    };

    // Calculate statistics
    const calculateStats = () => {
        const totalReturns = pagination.total;
        const totalAmount = returns.reduce((sum, r) => sum + parseFloat(r.grand_total || 0), 0);
        const refundedAmount = returns.reduce((sum, r) => sum + parseFloat(r.paid_amount || 0), 0);
        const pendingReturns = returns.filter(r => r.status === 'pending').length;
        const completedReturns = returns.filter(r => r.status === 'completed').length;

        return {
            totalReturns,
            totalAmount: totalAmount.toFixed(2),
            refundedAmount: refundedAmount.toFixed(2),
            pendingReturns,
            completedReturns
        };
    };

    const stats = calculateStats();

    // Calculate current page items indices
    const indexOfFirstItem = returns.length > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0;
    const indexOfLastItem = returns.length > 0 ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Sales Returns</h4>
                        <h6>Manage your sales returns and refunds</h6>
                    </div>
                    <ul className="table-top-head">
                        <li>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => navigate('/add-sales-return')}
                            >
                                <PlusCircle className="me-2" size={18} />
                                New Return
                            </button>
                        </li>
                        <li>
                            <button className="btn btn-outline-primary">
                                <Download className="me-2" size={18} />
                                Export
                            </button>
                        </li>
                        <li>
                            <button className="btn btn-outline-secondary">
                                <Printer className="me-2" size={18} />
                                Print
                            </button>
                        </li>
                    </ul>
                </div>

                {/* Statistics Cards */}
                <div className="row mb-4">
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Total Returns</h5>
                                <h3 className="card-text text-primary">{stats.totalReturns}</h3>
                                <p className="card-text text-muted">All time</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Return Amount</h5>
                                <h3 className="card-text text-danger">{formatCurrency(stats.totalAmount)}</h3>
                                <p className="card-text text-muted">Total value</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Refunded</h5>
                                <h3 className="card-text text-success">{formatCurrency(stats.refundedAmount)}</h3>
                                <p className="card-text text-muted">Amount refunded</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Pending</h5>
                                <h3 className="card-text text-warning">{stats.pendingReturns}</h3>
                                <p className="card-text text-muted">Awaiting processing</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <Search size={18} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by reference, invoice..."
                                        value={searchTerm}
                                        onChange={handleSearchInput}
                                        onKeyPress={handleKeyPress}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find(opt => opt.value === filters.status)}
                                    onChange={(opt) => handleFilterChange('status', opt?.value || '')}
                                    placeholder="All Status"
                                    isClearable
                                />
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                <Select
                                    options={customers}
                                    value={customers.find(c => c.value === filters.customer_id)}
                                    onChange={(opt) => handleFilterChange('customer_id', opt?.value || 0)}
                                    placeholder="All Customers"
                                    isClearable
                                />
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <RangePicker
                                    className="form-control"
                                    format="DD/MM/YYYY"
                                    onChange={handleDateRangeChange}
                                    placeholder={['From Date', 'To Date']}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className="col-lg-2 text-end">
                                <button
                                    className="btn btn-outline-secondary w-100"
                                    onClick={resetFilters}
                                >
                                    <Filter className="me-2" size={18} />
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Returns Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading returns...</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th onClick={() => handleSort('reference_no')} style={{ cursor: 'pointer' }}>
                                                    Reference No
                                                    {sortConfig.key === 'reference_no' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('original_invoice_no')} style={{ cursor: 'pointer' }}>
                                                    Original Invoice
                                                    {sortConfig.key === 'original_invoice_no' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('return_date')} style={{ cursor: 'pointer' }}>
                                                    Return Date
                                                    {sortConfig.key === 'return_date' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('customer_name')} style={{ cursor: 'pointer' }}>
                                                    Customer
                                                    {sortConfig.key === 'customer_name' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('grand_total')} style={{ cursor: 'pointer' }}>
                                                    Return Amount
                                                    {sortConfig.key === 'grand_total' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th>Status</th>
                                                <th>Refund Status</th>
                                                <th>Items/Qty</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {returns.length === 0 ? (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-5">
                                                        <div className="text-muted">
                                                            <RefreshCw size={48} className="mb-3" />
                                                            <h5>No returns found</h5>
                                                            <p>Create your first return to get started</p>
                                                            <button 
                                                                className="btn btn-primary"
                                                                onClick={() => navigate('/sales-returns/add')}
                                                            >
                                                                <PlusCircle size={16} className="me-2" />
                                                                New Return
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                returns.map((returnData) => (
                                                    <tr key={returnData.id}>
                                                        <td>
                                                            <strong>{returnData.reference_no}</strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                Created: {formatDate(returnData.created_at)}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            {returnData.original_invoice_no || 'N/A'}
                                                        </td>
                                                        <td>{formatDate(returnData.return_date)}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="avatar avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center text-white me-2">
                                                                    {returnData.customer_name?.charAt(0).toUpperCase()}
                                                                </div>
                                                                <div>
                                                                    {returnData.customer_name}
                                                                    <br />
                                                                    <small className="text-muted">{returnData.customer_phone || ''}</small>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <strong className="text-danger">{formatCurrency(returnData.grand_total)}</strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                Refunded: {formatCurrency(returnData.paid_amount || 0)}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <span className={getStatusBadge(returnData.status)}>
                                                                {getStatusIcon(returnData.status)} {returnData.status?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={getPaymentStatusBadge(returnData.payment_status)}>
                                                                {returnData.payment_status?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-info me-1">
                                                                Items: {returnData.items_count || 0}
                                                            </span>
                                                            <span className="badge bg-secondary">
                                                                Qty: {returnData.total_qty || 0}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="btn-group btn-group-sm" role="group">
                                                                <button
                                                                    className="btn btn-outline-info"
                                                                    onClick={() => handleView(returnData)}
                                                                    title="View Details"
                                                                >
                                                                    <Eye size={14} />
                                                                </button>
                                                                <button
                                                                    className="btn btn-outline-primary"
                                                                    onClick={() => handleEdit(returnData)}
                                                                    title="Edit"
                                                                    disabled={returnData.status === 'completed' || returnData.status === 'cancelled'}
                                                                >
                                                                    <Edit size={14} />
                                                                </button>
                                                                <button
                                                                    className="btn btn-outline-danger"
                                                                    onClick={() => handleDelete(returnData.id)}
                                                                    title="Delete"
                                                                    disabled={returnData.status === 'completed'}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {pagination.total > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            Showing <strong>{indexOfFirstItem}-{indexOfLastItem}</strong> of{' '}
                                            <strong>{pagination.total}</strong> returns
                                            <span className="text-muted ms-2">(Page {pagination.page} of {pagination.totalPages})</span>
                                        </div>
                                        <nav>
                                            <ul className="pagination mb-0">
                                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={handlePrevPage}
                                                        disabled={pagination.page === 1}
                                                    >
                                                        <ChevronLeft size={16} />
                                                    </button>
                                                </li>
                                                
                                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                    let pageNumber;
                                                    if (pagination.totalPages <= 5) {
                                                        pageNumber = i + 1;
                                                    } else if (pagination.page <= 3) {
                                                        pageNumber = i + 1;
                                                    } else if (pagination.page >= pagination.totalPages - 2) {
                                                        pageNumber = pagination.totalPages - 4 + i;
                                                    } else {
                                                        pageNumber = pagination.page - 2 + i;
                                                    }
                                                    
                                                    return (
                                                        <li 
                                                            key={pageNumber} 
                                                            className={`page-item ${pagination.page === pageNumber ? 'active' : ''}`}
                                                        >
                                                            <button 
                                                                className="page-link" 
                                                                onClick={() => handlePageChange(pageNumber)}
                                                            >
                                                                {pageNumber}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                                
                                                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={handleNextPage}
                                                        disabled={pagination.page === pagination.totalPages}
                                                    >
                                                        <ChevronRight size={16} />
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Modals */}
                {modalType === 'view' && selectedReturn && (
                    <ViewSaleReturn
                        returnData={selectedReturn}
                        onClose={handleModalClose}
                    />
                )}

                {modalType === 'edit' && selectedReturn && (
                    <EditSaleReturn
                        returnId={selectedReturn.id}
                        onSuccess={handleSuccess}
                        onClose={handleModalClose}
                        isOpen={true}
                    />
                )}
            </div>
        </div>
    );
};

export default SalesReturnList;